import React, { useEffect, useState } from 'react';
import { useStore } from '../../store';
import { projects as projectsApi } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import Avatar from '../UI/Avatar';
import { UserPlus, Trash2, Crown, Shield, User, Eye, X, Check, Lock, Link2, Copy } from 'lucide-react';

const ROLES = {
  owner: { label: 'Владелец', icon: Crown, color: 'text-amber-500' },
  admin: { label: 'Администратор', icon: Shield, color: 'text-brand-500' },
  member: { label: 'Участник', icon: User, color: 'text-gray-500' },
  viewer: { label: 'Наблюдатель', icon: Eye, color: 'text-gray-400' },
};

export default function MembersPanel({ project, onClose }) {
  const { updateProject, user } = useStore();
  const { canProjectManageMembers, getUserProjectRole } = usePermissions();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const canManage = canProjectManageMembers(project);
  const userRole = getUserProjectRole(project);

  const generateInviteLink = async (role = inviteRole) => {
    setGeneratingLink(true);
    try {
      const data = await projectsApi.generateInvite(project._id, role);
      setInviteLink(data.url);
      setError('');
    } catch (err) {
      setError(err.message || 'Ошибка при генерации ссылки');
    } finally {
      setGeneratingLink(false);
    }
  };

  useEffect(() => {
    if (!canManage || !project?._id) return;
    generateInviteLink(inviteRole);
  }, [project?._id, canManage, inviteRole]);

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(inviteLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const updated = await projectsApi.addMember(project._id, { email: inviteEmail.trim(), role: inviteRole });
      useStore.setState(s => ({ projects: s.projects.map(p => p._id === project._id ? updated : p), activeProject: updated }));
      setInviteEmail('');
      setSuccess(`Участник ${inviteEmail} добавлен`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Ошибка');
    } finally { setLoading(false); }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Удалить участника из проекта?')) return;
    try {
      const updated = await projectsApi.removeMember(project._id, userId);
      useStore.setState(s => ({ projects: s.projects.map(p => p._id === project._id ? updated : p), activeProject: updated }));
    } catch (err) { alert(err.message); }
  };

  const changeRole = async (userId, role) => {
    try {
      const updated = await projectsApi.updateMemberRole(project._id, userId, role);
      useStore.setState(s => ({ projects: s.projects.map(p => p._id === project._id ? updated : p), activeProject: updated }));
    } catch (err) { alert(err.message); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal p-6 max-w-lg animate-scale-in">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold">Участники проекта</h2>
            <p className="text-xs text-gray-400 mt-0.5">{project.name} · {project.members?.length || 0} чел.</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1"><X className="w-4 h-4" /></button>
        </div>

        {/* Invite form */}
        {canManage && (
          <>
            {/* Email invitation */}
            <div className="mb-5 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <label className="label">Пригласить по email</label>
              <div className="flex gap-2">
                <input className="input flex-1 text-sm" type="email" placeholder="colleague@example.com"
                  value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && invite()} />
                <select className="input w-32 text-sm" value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                  {Object.entries(ROLES).filter(([k]) => k !== 'owner').map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <button onClick={invite} disabled={loading || !inviteEmail} className="btn-primary px-3">
                  <UserPlus className="w-4 h-4" />
                </button>
              </div>
              {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
              {success && <p className="text-xs text-green-600 mt-2 flex items-center gap-1"><Check className="w-3 h-3" />{success}</p>}
              <p className="text-xs text-gray-400 mt-2">Пользователь должен быть зарегистрирован в системе</p>
            </div>

            {/* Invite link generation */}
            <div className="mb-5 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <label className="label">Поделиться ссылкой приглашения</label>
              {generatingLink && !inviteLink ? (
                <div className="flex items-center justify-center gap-2 py-3 text-sm text-gray-500">
                  <Link2 className="w-4 h-4 animate-pulse" />
                  Генерируем ссылку...
                </div>
              ) : inviteLink ? (
                <div className="space-y-2">
                  <div className="flex gap-2 items-center bg-white dark:bg-gray-900 p-2 rounded-lg border border-gray-200 dark:border-gray-700">
                    <input
                      type="text"
                      value={inviteLink}
                      readOnly
                      className="input flex-1 text-xs py-2"
                    />
                    <button
                      onClick={copyLinkToClipboard}
                      className="btn-ghost p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      {linkCopied ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">
                    {linkCopied ? '✓ Скопировано!' : 'Откройте эту ссылку или отправьте её другим'}
                  </p>
                  <button
                    onClick={() => generateInviteLink(inviteRole)}
                    disabled={generatingLink}
                    className="btn-ghost text-xs text-gray-500 hover:text-gray-700"
                  >
                    Создать новую ссылку
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => generateInviteLink(inviteRole)}
                  disabled={generatingLink}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                >
                  <Link2 className="w-4 h-4" />
                  Повторить генерацию ссылки
                </button>
              )}
            </div>
          </>
        )}
        {!canManage && (
          <div className="mb-5 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex gap-3">
            <Lock className="w-4 h-4 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-100">У вас нет прав для управления участниками проекта</p>
          </div>
        )}

        {/* Members list */}
        <div className="space-y-1">
          {(project.members || []).map(member => {
            const u = member.user;
            if (!u) return null;
            const roleInfo = ROLES[member.role] || ROLES.member;
            const RoleIcon = roleInfo.icon;
            const isSelf = u._id === user?._id;
            const isThisOwner = member.role === 'owner';

            return (
              <div key={u._id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
                <Avatar name={u.name} color={u.color} src={u.avatar} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium">{u.name}</span>
                    {isSelf && <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">вы</span>}
                  </div>
                  <span className="text-xs text-gray-400">{u.email}</span>
                </div>

                {canManage && !isThisOwner && !isSelf ? (
                  <select
                    className="input text-xs w-36 py-1"
                    value={member.role}
                    onChange={e => changeRole(u._id, e.target.value)}>
                    {Object.entries(ROLES).filter(([k]) => k !== 'owner').map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                ) : (
                  <span className={`flex items-center gap-1 text-xs font-medium ${roleInfo.color}`}>
                    <RoleIcon className="w-3 h-3" />{roleInfo.label}
                  </span>
                )}

                {canManage && !isThisOwner && !isSelf && (
                  <button onClick={() => removeMember(u._id)}
                    className="opacity-0 group-hover:opacity-100 btn-ghost p-1.5 text-red-400 hover:text-red-500 transition-all">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
